import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, CheckCheck, Paperclip, Send, Trash2 } from "lucide-react";
import { ApiError, downloadFile } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Button, Input } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { uploadAttachment, attachmentUrl, deleteAttachment } from "@/features/tickets/api";
import {
  useChatRoom,
  useMarkChatRead,
  usePostMessage,
  useRoomMessages,
  useTypingIndicator,
  useTypingSignal,
} from "./hooks";

/**
 * Ticket chat, shown inside the ticket detail dialog.
 *
 * Messages arrive live over the WebSocket (which invalidates the message
 * query); typing indicators are ephemeral DOM events; read receipts show a
 * double-check once the other side has read a message. File sharing reuses the
 * ticket attachment pipeline — upload, then send the attachment id.
 */
export function ChatPanel({ ticketId }: { ticketId: string }) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const { data: room } = useChatRoom(ticketId);
  const roomId = room?.id ?? null;

  async function removeAttachment(attachmentId: string) {
    if (!window.confirm("Delete this file? This can't be undone.")) return;
    try {
      await deleteAttachment(attachmentId);
      void qc.invalidateQueries({ queryKey: ["chat", roomId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the file.");
    }
  }
  const { data: messages } = useRoomMessages(roomId, room?.messages ?? []);
  const post = usePostMessage();
  const markRead = useMarkChatRead();
  const signalTyping = useTypingSignal(roomId);
  const typingWho = useTypingIndicator(roomId);

  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Mark read whenever the panel shows new messages from someone else.
  useEffect(() => {
    if (!roomId || !messages) return;
    const hasOthers = messages.some((m) => m.sender_id !== user?.id);
    if (hasOthers) void markRead.mutate(roomId);
    // Scroll to the newest message.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, messages?.length]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!roomId || !text.trim()) return;
    setError(null);
    try {
      await post.mutateAsync({ roomId, body: { body: text.trim() } });
      setText("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send.");
    }
  }

  async function attach(file: File) {
    if (!roomId) return;
    setError(null);
    try {
      // Reuse the ticket upload (magic-byte validated), then reference it.
      const uploaded = await uploadAttachment(ticketId, file);
      await post.mutateAsync({
        roomId,
        body: { body: text.trim(), attachment_id: uploaded.id },
      });
      setText("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not share the file.");
    }
  }

  return (
    <section className="flex flex-col rounded-md border border-border">
      <div className="border-b border-border px-3 py-2 text-sm font-medium">Chat</div>

      <div ref={scrollRef} className="max-h-64 min-h-[8rem] space-y-2 overflow-y-auto p-3">
        {(messages ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">
            No messages yet. Start the conversation.
          </p>
        )}
        {(messages ?? []).map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${
                  mine ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
              >
                {!mine && (
                  <div className="text-xs font-medium opacity-80">{m.sender_name}</div>
                )}
                {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                {m.attachment && (
                  <div className="mt-1 flex items-center gap-1">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs underline"
                      onClick={() =>
                        void downloadFile(
                          attachmentUrl(m.attachment!.id),
                          m.attachment!.file_name,
                        )
                      }
                    >
                      <Paperclip className="h-3 w-3" aria-hidden="true" />
                      {m.attachment.file_name}
                    </button>
                    {(mine || can("ticket:update")) && (
                      <button
                        type="button"
                        aria-label={`Delete ${m.attachment.file_name}`}
                        title="Delete file"
                        className="rounded p-0.5 opacity-70 hover:text-destructive hover:opacity-100"
                        onClick={() => void removeAttachment(m.attachment!.id)}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}
                <div
                  className={`mt-0.5 flex items-center gap-1 text-[10px] ${
                    mine ? "text-primary-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {formatDateTime(m.sent_at)}
                  {mine &&
                    (m.read_by_others ? (
                      <CheckCheck className="h-3 w-3" aria-label="Read" />
                    ) : (
                      <Check className="h-3 w-3" aria-label="Sent" />
                    ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-4 px-3 text-xs italic text-muted-foreground" aria-live="polite">
        {typingWho ? `${typingWho} is typing…` : ""}
      </div>

      {error && (
        <div role="alert" className="px-3 pb-1 text-xs text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-2">
        <label className="cursor-pointer rounded-md p-2 hover:bg-accent" aria-label="Attach a file">
          <Paperclip className="h-4 w-4" aria-hidden="true" />
          <input
            type="file"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void attach(file);
              e.target.value = "";
            }}
          />
        </label>
        <Input
          aria-label="Message"
          placeholder="Type a message…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            signalTyping();
          }}
        />
        <Button type="submit" aria-label="Send" disabled={!text.trim() || post.isPending}>
          <Send className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>
    </section>
  );
}
